import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import { DAILY, MONTHLY, WEEKLY } from '../../Constants/Signal';
import {
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  COLUMN_GRAPH,
  GAUGE,
  HIGH_BAR,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  PIE,
  TEXT_ENGINE,
  TEXT_FUEL,
  VERTICAL_COLUMN_GRAPH,
} from '../../Constants/WidgetTypes';
import { SocketContext } from '../../Context/DashboardSocketContext';

import AssetDashboardDisplay from './AssetDashboardDisplay';

export default memo(forwardRef(AssetDashboardContainer));

const GRAPH_WIDGETS = new Set([
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  COLUMN_GRAPH,
  GAUGE,
  HIGH_BAR,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  PIE,
  VERTICAL_COLUMN_GRAPH,
]);

const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

const cols = {
  xs: 1,
  sm: 1,
  md: 1,
  lg: 3,
  xl: 3,
};

const aggregateIntervalMap = new Map([
    [DAILY, 'day'],
    [MONTHLY, 'month'],
    [WEEKLY, 'week'],
  ]),
  initState = {
    dashboardSearchTerm: '',
    dataLoading: true,
    error: '',
    fetchedData: new Map(),
    graphWidgets: [],
    historicalUtilizationData: new Map(),
    layout: [],
    nonGraphWidgets: [],
    signalNamesMap: new Map(),
    signalUnitsMap: new Map(),
    utilizationData: new Map(),
  },
  utilizationWidgetSet = new Set([TEXT_ENGINE, TEXT_FUEL]);

function AssetDashboardContainer(props, ref) {
  const socket = useContext(SocketContext);
  const initDataLoaded = useRef(false);
  const [state, updateState] = useState(initState);

  const parseWidgetDataResponse = useCallback(
      (data, widgets) => {
        const fetchedData = Object.keys(data || {})?.length
          ? new Map(Object.entries(data).filter(([key]) => key !== 'undefined'))
          : new Map();
        const { signalNamesMap, signalUnitsMap } = createSignalsMaps(
          props.asset?.gtemplate?.datapoints,
          fetchedData
        );
        const { graphWidgets = [], nonGraphWidgets = [] } =
          parseWidgetTypes(widgets || []) || {};
        const layout = createGridLayouts(graphWidgets, nonGraphWidgets);

        updateState((prevState) => ({
          ...prevState,
          dataLoading: false,
          fetchedData,
          graphWidgets,
          layout,
          nonGraphWidgets,
          signalNamesMap,
          signalUnitsMap,
        }));
      },
      [props.asset?.gtemplate?.datapoints]
    ),
    fetchWidgetData = useCallback(
      ({
        dashboardID = props.currentDashboard?._id,
        widgets = props.widgets,
      } = {}) => {
        try {
          initDataLoaded.current = true;

          if (socket?.disconnected) socket?.connect?.();

          if (!state.dataLoading) {
            updateState((prevState) => ({ ...prevState, dataLoading: true }));
          }

          socket
            ?.on?.('connect_error', (err) => {
              throw err;
            })
            ?.on?.('error', (err) => {
              throw err;
            })
            ?.emit?.('dashboard:initial', { dashboardID }, (data) => {
              parseWidgetDataResponse(data, widgets);
            })
            ?.emit?.('dashboard:realtime', { dashboardID }, ({ error }) => {
              if (error) throw new Error(error.message);
            });

          if (widgets?.some?.(({ type }) => utilizationWidgetSet.has(type))) {
            socket?.emit?.('utilization:static', null, (data) => {
              const utilizationData = new Map(
                Object.entries(data || {}).reduce(
                  (map, [period, signals]) => map.set(period, signals),
                  new Map()
                )
              );
              updateState((prevState) => ({ ...prevState, utilizationData }));
            });
          }

          if (
            widgets?.some?.(({ settings }) => settings?.aggregation === DAILY)
          ) {
            const now = new Date();
            socket?.emit?.(
              'utilization:report',
              {
                dateStart: subDays(now, 7).toISOString(),
                dateStop: now.toISOString(),
                interval: aggregateIntervalMap.get(DAILY),
              },
              (data) => {
                updateState((prevState) => {
                  const dailyData = new Map(
                    Object.entries(data || {}).reduce(
                      (map, [dateStr, signals]) => map.set(dateStr, signals),
                      new Map()
                    )
                  );
                  const historicalUtilizationData = new Map(
                    prevState.historicalUtilizationData
                  ).set(DAILY, dailyData);

                  return { ...prevState, historicalUtilizationData };
                });
              }
            );
          }
          if (
            widgets?.some?.(({ settings }) => settings?.aggregation === WEEKLY)
          ) {
            const now = new Date();
            socket?.emit?.(
              'utilization:report',
              {
                dateStart: subWeeks(now, 5).toISOString(),
                dateStop: now.toISOString(),
                interval: aggregateIntervalMap.get(WEEKLY),
              },
              (data) => {
                updateState((prevState) => {
                  const dailyData = new Map(
                    Object.entries(data || {}).reduce(
                      (map, [dateStr, signals]) => map.set(dateStr, signals),
                      new Map()
                    )
                  );
                  const historicalUtilizationData = new Map(
                    prevState.historicalUtilizationData
                  ).set(WEEKLY, dailyData);

                  return { ...prevState, historicalUtilizationData };
                });
              }
            );
          }
          if (
            widgets?.some?.(({ settings }) => settings?.aggregation === MONTHLY)
          ) {
            const now = new Date();
            socket?.emit?.(
              'utilization:report',
              {
                dateStart: subMonths(now, 5).toISOString(),
                dateStop: now.toISOString(),
                interval: aggregateIntervalMap.get(MONTHLY),
              },
              (data) => {
                updateState((prevState) => {
                  const dailyData = new Map(
                    Object.entries(data || {}).reduce(
                      (map, [dateStr, signals]) => map.set(dateStr, signals),
                      new Map()
                    )
                  );
                  const historicalUtilizationData = new Map(
                    prevState.historicalUtilizationData
                  ).set(MONTHLY, dailyData);

                  return { ...prevState, historicalUtilizationData };
                });
              }
            );
          }
        } catch (err) {
          const error = err.response?.data?.message ?? err.message;
          updateState((prevState) => ({
            ...prevState,
            error,
            dataLoading: false,
          }));
        }
      },
      [
        parseWidgetDataResponse,
        props.currentDashboard?._id,
        props.widgets,
        socket,
        state.dataLoading,
      ]
    );

  const handlePropUpdate = useCallback(({ target: { name, value } = {} }) => {
    updateState((prevState) => ({ ...prevState, [name]: value }));
  }, []);

  useImperativeHandle(ref, () => ({ fetchWidgetData }));

  useEffect(() => {
    if (initDataLoaded?.current) return;
    fetchWidgetData();
  }, [fetchWidgetData]);

  return state.dataLoading ? (
    <div className="align-items-center d-flex h-100 jsx-58948489 justify-content-center w-100">
      <FontAwesomeIcon
        color="var(--brand-primary)"
        icon={faCircleNotch}
        size="3x"
        spin
      />
    </div>
  ) : (
    <AssetDashboardDisplay
      {...state}
      asset={props.asset}
      breakpoints={breakpoints}
      cols={cols}
      currentDashboard={props.currentDashboard}
      handleAddUsersSave={props.handleAddUsersSave}
      handleChangeCurrentDashboard={props.handleChangeCurrentDashboard}
      handleCommandExecutedFromWidget={props.handleCommandExecutedFromWidget}
      handleDashboardCreated={props.handleDashboardCreated}
      handleDashboardWidgetDeleted={props.handleDashboardWidgetDeleted}
      handleEditDashboardSave={props.handleEditDashboardSave}
      handlePropUpdate={handlePropUpdate}
      handleTemplateDashboardDeleted={props.handleTemplateDashboardDeleted}
      handleWidgetAdded={props.handleWidgetAdded}
      handleWidgetDeleted={props.handleWidgetDeleted}
      handleWidgetUpdated={props.handleWidgetUpdated}
      handleWidgetsUpdate={props.handleWidgetsUpdate}
      showAddUsersModal={props.showAddUsersModal}
      showCreateDashboardModal={props.showCreateDashboardModal}
      showCreateWidgetModal={props.showCreateWidgetModal}
      showDeleteDashboardModal={props.showDeleteDashboardModal}
      showEditDashboardModal={props.showEditDashboardModal}
      showViewTemplateDashboardsModal={props.showViewTemplateDashboardsModal}
      templateDashboards={props.templateDashboards}
      toggleAddUsersModal={props.toggleAddUsersModal}
      toggleCreateDashboardModal={props.toggleCreateDashboardModal}
      toggleCreateWidgetModal={props.toggleCreateWidgetModal}
      toggleDeleteDashboardModal={props.toggleDeleteDashboardModal}
      toggleEditDashboardModal={props.toggleEditDashboardModal}
      toggleViewTemplateDashboardsModal={
        props.toggleViewTemplateDashboardsModal
      }
      widgets={props.widgets}
    />
  );
}

AssetDashboardContainer.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    dashboard: PropTypes.shape({
      _id: PropTypes.string,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
    }),
    format: PropTypes.string,
    gtemplate: PropTypes.shape({
      _id: PropTypes.string,
      commands: PropTypes.array,
      datapoints: PropTypes.arrayOf(PropTypes.object),
      description: PropTypes.string,
      links: PropTypes.array,
      name: PropTypes.string,
      orgID: PropTypes.string,
      settings: PropTypes.object,
      widgets: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          col: PropTypes.number,
          row: PropTypes.number,
          settings: PropTypes.object,
          sizeX: PropTypes.number,
          sizeY: PropTypes.number,
        })
      ),
    }),
    gtemplateID: PropTypes.string,
    shadow: PropTypes.object,
  }),
  currentDashboard: PropTypes.shape({
    _id: PropTypes.string,
    gtemplateID: PropTypes.string,
    isDefault: PropTypes.bool,
    isPrivate: PropTypes.bool,
    name: PropTypes.string,
    orgID: PropTypes.string,
    widgets: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        col: PropTypes.number,
        row: PropTypes.number,
        settings: PropTypes.object,
        sizeX: PropTypes.number,
        sizeY: PropTypes.number,
      })
    ),
  }),
  handleAddUsersSave: PropTypes.func,
  handleChangeCurrentDashboard: PropTypes.func,
  handleCommandExecutedFromWidget: PropTypes.func,
  handleDashboardCreated: PropTypes.func,
  handleDashboardWidgetDeleted: PropTypes.func,
  handleEditDashboardSave: PropTypes.func,
  handleTemplateDashboardDeleted: PropTypes.func,
  handleWidgetAdded: PropTypes.func,
  handleWidgetDeleted: PropTypes.func,
  handleWidgetUpdated: PropTypes.func,
  handleWidgetsUpdate: PropTypes.func,
  showAddUsersModal: PropTypes.bool,
  showCreateDashboardModal: PropTypes.bool,
  showCreateWidgetModal: PropTypes.bool,
  showDeleteDashboardModal: PropTypes.bool,
  showEditDashboardModal: PropTypes.bool,
  showViewTemplateDashboardsModal: PropTypes.bool,
  templateDashboards: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      gtemplateID: PropTypes.string,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
      orgID: PropTypes.string,
      widgets: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          col: PropTypes.number,
          row: PropTypes.number,
          settings: PropTypes.object,
          sizeX: PropTypes.number,
          sizeY: PropTypes.number,
        })
      ),
    })
  ),
  toggleAddUsersModal: PropTypes.func,
  toggleCreateDashboardModal: PropTypes.func,
  toggleCreateWidgetModal: PropTypes.func,
  toggleDeleteDashboardModal: PropTypes.func,
  toggleEditDashboardModal: PropTypes.func,
  toggleViewTemplateDashboardsModal: PropTypes.func,
  widgets: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      col: PropTypes.number,
      row: PropTypes.number,
      settings: PropTypes.object,
      sizeX: PropTypes.number,
      sizeY: PropTypes.number,
    })
  ),
};

AssetDashboardContainer.defaultProps = {};

function createSignalsMaps(datapoints, fetchedData) {
  const defaultValue = {
    signalUnitsMap: new Map(),
    signalNamesMap: new Map(),
  };
  if (isEmpty(datapoints)) return defaultValue;

  return datapoints.reduce(
    (map, collection) => {
      if (isEmpty(collection?.values)) return map;

      const [{ signals } = {}] = fetchedData.get(collection._id) || [];
      const unitsValue = collection.values.reduce(
        (acc, { _id, display_precision, label, name, unit }) => {
          map.signalNamesMap.set(_id, name || label);

          const { uom = '' } = signals?.[_id] || {};

          acc[name || label] = { display_precision, unit: uom || unit };
          return acc;
        },
        {}
      );

      map.signalUnitsMap.set(collection._id, unitsValue);
      return map;
    },
    { signalUnitsMap: new Map(), signalNamesMap: new Map() }
  );
}

function parseWidgetTypes(widgets) {
  return widgets.reduce(
    (acc, w) => {
      if (GRAPH_WIDGETS.has(w.type)) {
        acc.graphWidgets.push(w);
      } else {
        acc.nonGraphWidgets.push(w);
      }
      return acc;
    },
    { graphWidgets: [], nonGraphWidgets: [] }
  );
}

function createGridLayouts(graphWidgets, nonGraphWidgets) {
  if (!graphWidgets.length && !nonGraphWidgets.length) return [];

  return [...graphWidgets, ...nonGraphWidgets].map((w) => {
    const { _id, col, row, sizeX, sizeY } = w;
    return {
      i: _id,
      x: col,
      y: row,
      h: sizeY,
      w: sizeX,
    };
  });
}
